import { createMentionPluginInstance } from '../TextInputMention/TextInputMention';
import { ContentState, EditorState, Modifier, SelectionState } from 'draft-js';

export const getInitialEditorState = note => {
  if (!note?.get('text')) {
    return EditorState.createEmpty();
  }

  try {
    const contentState = createContentStateFromNote(note);
    const mentionPlugin = createMentionPluginInstance();
    return EditorState.createWithContent(contentState, mentionPlugin.decorator);
  } catch (error) {
    console.error('Build Note Error', error);
    return EditorState.createEmpty();
  }
};

export const createContentStateFromNote = note => {
  const text = note.get('text');
  const taggedParties = note.get('taggedParties').toJS();

  let contentState = ContentState.createFromText(text);
  const blockMap = contentState.getBlockMap();
  const blocks = blockMap.toArray();

  taggedParties.forEach(tag => {
    const { startIndex, endIndex } = tag;
    let accumulated = 0;

    for (const block of blocks) {
      const blockKey = block.getKey();
      const blockLength = block.getLength();

      if (startIndex >= accumulated && startIndex < accumulated + blockLength) {
        const relativeStart = startIndex - accumulated;
        const relativeEnd = endIndex - accumulated;

        const entityKey = contentState.createEntity('mention', 'IMMUTABLE', { mention: tag }).getLastCreatedEntityKey();

        const selection = SelectionState.createEmpty(blockKey).merge({
          anchorOffset: relativeStart,
          focusOffset: relativeEnd,
        });

        contentState = Modifier.applyEntity(contentState, selection, entityKey);
        break;
      }

      accumulated += blockLength + 1;
    }
  });

  return contentState;
};

const mapTaggedUsersArray = inputData => {
  const usersTagged = inputData.entityMap;
  const totalBlocks = inputData.blocks;
  const entityRangesArray = totalBlocks
    .map(item => {
      return item.entityRanges.map(item => item);
    })
    .flat();

  let totalTextLengthByBlock = 0;
  const offsetsArray = inputData.blocks
    .map(item => {
      const { entityRanges, text } = item;
      const dataOffsets = entityRanges.map(item => {
        return item.offset + totalTextLengthByBlock;
      });
      totalTextLengthByBlock += text.length;
      return dataOffsets;
    })
    .flat();

  const taggedUsersArray = Object.keys(usersTagged).map((key, index) => {
    const { type, _id, accountId, active } = usersTagged[key].data.mention;
    const { length } = entityRangesArray[key];
    const offsetText = offsetsArray[index];

    return {
      type: type,
      startIndex: offsetText,
      endIndex: offsetText + length,
      _id: type === 'account' ? accountId : _id,
      accountId: accountId,
      active,
    };
  });

  return taggedUsersArray;
};

export const getPlainTextAndTagsFromHTML = (htmlText, inputData) => {
  const originalRelatedParties = mapTaggedUsersArray(inputData);
  const regexTaggedParties = new RegExp(`class='taggedNote'[^>]*>(@[^<]+)<\\/span>`, 'ig');
  const originalRelatedPartiesIterator = originalRelatedParties.entries();
  let result = null;
  let originalRelatedParty = null;
  const relatedParties = [];

  while (
    (result = regexTaggedParties.exec(htmlText)) != null &&
    !(originalRelatedParty = originalRelatedPartiesIterator.next()).done
  ) {
    const [, relatedPartyName] = result;
    relatedParties.push({
      ...originalRelatedParty.value[1],
      name: relatedPartyName,
    });
  }

  const htmlTextWithBreakLines = htmlText.replace(/<\/?br\s*>/gi, `\n`);
  const rawTextMultipleSpaces = htmlTextWithBreakLines.replace(/<[^>]+>/gi, '');
  const rawText = rawTextMultipleSpaces
    .replace(/ +/gi, ' ')
    .replace(/ *\n */gi, '\n')
    .trim();

  const relatedPartiesFullInfo = [];
  let lastIndex = 0;
  relatedParties.forEach(relatedParty => {
    const startIndex = rawText.indexOf(relatedParty.name, lastIndex);
    const endIndex = startIndex + relatedParty.name.length;
    lastIndex = endIndex;
    relatedPartiesFullInfo.push({ ...relatedParty, startIndex, endIndex });
  });

  return { plainText: rawText, taggedParties: relatedPartiesFullInfo };
};

export const removeFieldsForPayload = taggedParties => {
  if (!taggedParties) return [];

  return taggedParties.map(({ active, accountId, name, ...rest }) => rest);
};
